import React from "react";
import { Box, Button, Stack } from "@mui/joy";
import { Navigate, useParams } from "react-router-dom";
import { theme } from "styles/theme";
import FullPageContainer from "pages/container/FullPageContainer";
import CustomTypography from "components/common/CustomTypography";
import { LogoWithTextSvg } from "static/image/common/SvgAssets";
import { deepLinkData } from "data/deepLinkData";

const KAKAO_CHANNEL_PUBLIC_ID = "_xjxfLVG";
const KAKAO_CHANNEL_LINK_TEXT = "디스턴싱 카카오채널";

// 가입 환영 레터 시리즈 (인증 없이 접근 가능한 원페이지).
// 새 레터는 LETTERS 에 항목만 추가하면 /letter/<key> 로 열린다.
type LetterContent = {
  greeting?: string;
  paragraphsBeforeVideo: string[];
  youtubeVideoId: string;
  paragraphsAfterVideo: string[];
};

const LETTERS: Record<string, LetterContent> = {
  "1": {
    greeting: "안녕하세요, 디스턴싱에 가입하신 것을 환영합니다.",
    paragraphsBeforeVideo: [
      "디스턴싱에 가입하셨다는 건, 지금보다 더 나은 삶을 만들어보고 싶은 마음이 있었기 때문이라고 생각해요. 저희가 그 과정에서 최선을 다해 돕겠습니다.",
      "내 삶의 문제를 궁극적으로 해결하고 회복하기 위해 가장 중요한 것은, 결국 '내가 나 자신의 치료자가 되는 것'입니다. 마법처럼 단번에 삶을 바꿔주는 약은 없지만, 내가 스스로를 돌보기로 선택하고 노력한다면 분명 변화할 수 있을 거예요. 아래 유튜브 영상에서 이에 대해 조금 더 자세히 이야기를 해 보았답니다.",
    ],
    youtubeVideoId: "lduFQzN4oxs",
    paragraphsAfterVideo: [
      "앞으로 총 세 번의 영상을 통해 중요한 이야기들을 전하려고 해요. 앞으로 이어질 두 편의 영상도 받아보시려면 디스턴싱 카카오채널을 친구 추가해 주세요!",
    ],
  },
  "2": {
    paragraphsBeforeVideo: [
      "지난 영상에 이어, 이번에는 어떻게 '내 삶의 나침반이 되어 스스로를 돌볼 수 있는지' 그 원리를 말씀드리려 합니다.",
      "핵심은 내면의 괴로운 생각에 휘둘리지 않고, 한 걸음 물러서서 '생각과 거리를 두는 마음의 힘'을 기르는 것입니다. 물론 그 과정이 쉽진 않답니다. 때론 불편하기도 할 거예요. 하지만 하나씩 노력한다면 분명 마음의 근육을 키울 수 있는 방법이 있답니다.",
      "이번 영상에서는 디스턴싱에서 무엇을 하고, 어떻게 프로그램을 진행하는지에 대해 간단히 소개해 보았어요.",
    ],
    youtubeVideoId: "TKj_SzNHQw8",
    paragraphsAfterVideo: [],
  },
  "3": {
    paragraphsBeforeVideo: [
      "혹시 그동안 부정적인 감정을 '없애는 것'에만 온 마음을 쏟고 계시진 않았나요? 하지만 우리 삶의 진짜 목적은 우울과 불안을 지우는 것이 아니라, '내가 진정으로 원하는 삶을 살아나가는 것'입니다. 괴로운 감정이 찾아오더라도 휘둘리지 않고, 내게 소중한 가치를 향해 한 걸음씩 나아갈 수 있는 힘은 이미 여러분 안에 있습니다.",
      "여러분이 우울을 넘어 '원하는 삶'으로 전념할 수 있도록 돕는 마지막 이야기를 지금 확인해 보세요. 여러분이 언제든 변화를 선택하신다면, 디스턴싱은 늘 이곳에서 최선을 다해 도울 준비가 되어 있습니다.",
    ],
    youtubeVideoId: "QZkd50OOH_g",
    paragraphsAfterVideo: [],
  },
};

function YoutubeEmbed({ videoId }: { videoId: string }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 0,
        // 16:9 비율 확보 (aspect-ratio 미지원 구형 WebView 대응)
        paddingBottom: "56.25%",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Surface.Backward,
      }}
    >
      <Box
        component="iframe"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="유튜브 영상"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
    </Box>
  );
}

function KakaoChannelLink() {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // SDK 초기화가 되어 있으면 공식 친구추가 다이얼로그, 아니면 href(채널 친구추가 웹 페이지)로 진행
    if (window.Kakao?.isInitialized?.()) {
      e.preventDefault();
      window.Kakao.Channel.addChannel({
        channelPublicId: KAKAO_CHANNEL_PUBLIC_ID,
      });
    }
  };

  return (
    <Box
      component="a"
      href={`https://pf.kakao.com/${KAKAO_CHANNEL_PUBLIC_ID}/friend`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{
        // highlight 색 + 밑줄로 링크 표시
        color: theme.vars.palette.Content.Highlight,
        textDecoration: "underline",
        textUnderlineOffset: "3px",
      }}
    >
      {KAKAO_CHANNEL_LINK_TEXT}
    </Box>
  );
}

// 문단 안의 "디스턴싱 카카오채널" 문구를 친구 추가 링크로 치환해 렌더
function renderParagraphWithChannelLink(paragraph: string) {
  const parts = paragraph.split(KAKAO_CHANNEL_LINK_TEXT);
  if (parts.length === 1) {
    return paragraph;
  }
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && <KakaoChannelLink />}
      {part}
    </React.Fragment>
  ));
}

export default function LetterPage() {
  const { letterId } = useParams();
  const letter = letterId ? LETTERS[letterId] : undefined;

  if (!letter) {
    return <Navigate to="/" replace />;
  }

  // 본문에 카카오채널 추가 요청이 있는 레터(현재 1번)는 채널 추가 전환에 집중하도록
  // 앱 진입 CTA 를 노출하지 않는다
  const hasKakaoAsk = [
    ...letter.paragraphsBeforeVideo,
    ...letter.paragraphsAfterVideo,
  ].some((paragraph) => paragraph.includes(KAKAO_CHANNEL_LINK_TEXT));

  return (
    <FullPageContainer
      backgroundColor={theme.vars.palette.Background.Level1}
      hasFooter={false}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          height: "100%",
          overflowY: "auto",
          px: 3,
          pt: "48px",
          pb: "64px",
        }}
      >
        <Stack spacing="40px">
          {/* CustomTypography 의 기본 px(4px)와 좌측 라인을 맞춘다 */}
          <Box sx={{ pl: "4px" }}>
            <LogoWithTextSvg />
          </Box>

          <Stack spacing="24px">
            {letter.greeting && (
              <CustomTypography
                variant="Subtitle"
                fontWeight={700}
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  lineHeight: "150%",
                }}
              >
                {letter.greeting}
              </CustomTypography>
            )}

            {letter.paragraphsBeforeVideo.map((paragraph) => (
              <CustomTypography
                key={paragraph}
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: "170%",
                }}
              >
                {renderParagraphWithChannelLink(paragraph)}
              </CustomTypography>
            ))}

            <YoutubeEmbed videoId={letter.youtubeVideoId} />

            {letter.paragraphsAfterVideo.map((paragraph) => (
              <CustomTypography
                key={paragraph}
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: "170%",
                }}
              >
                {renderParagraphWithChannelLink(paragraph)}
              </CustomTypography>
            ))}
          </Stack>

          {!hasKakaoAsk && (
            // 본문 텍스트의 좌우 라인(CustomTypography 기본 px 4px)과 버튼 폭을 맞춘다
            <Box sx={{ px: "4px" }}>
              <Button
                onClick={() => {
                  window.location.href = deepLinkData.install.url;
                }}
                variant="Solid"
                color="primary"
                size="lg"
                sx={{
                  width: "100%",
                  height: "54px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "16px",
                }}
              >
                디스턴싱 시작하기
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </FullPageContainer>
  );
}
